import { z } from 'zod';

/**
 * Plan limits and features, heavily inspired by Plesk service plans but
 * expressed in Coolify terms. A service plan carries a set of limits and
 * feature toggles; a subscription may override individual limit values
 * (Plesk calls this "customizing" a subscription — the plan stays the
 * source of truth for everything not explicitly overridden).
 *
 * Since G4 (ADR-0011) the key set is open: modules register limit and
 * feature *descriptors* at boot (apps/api/src/core/plan-descriptors.ts) and
 * validation, the plan editor, override editing, usage reporting and
 * enforcement all render from the registry instead of a hardcoded field
 * list. This file keeps the value-level vocabulary (what a limit value IS),
 * the core module's descriptor data, and the pure helpers.
 *
 * `null` means unlimited, matching Plesk's "Unlimited" toggle.
 */

export const limitValueSchema = z.number().int().min(0).nullable();
export type LimitValue = z.infer<typeof limitValueSchema>;

/** How a limit is measured; drives editor rendering and formatting. */
export const LIMIT_KINDS = ['count', 'megabytes'] as const;
export type LimitKind = (typeof LIMIT_KINDS)[number];

/**
 * A limit a module contributes to every plan. Descriptors are registration
 * data, not per-plan state: the per-plan values live in the plan's JSONB.
 */
export interface LimitDescriptor {
  /** JSON key in plan limits; non-core modules must prefix it (ADR-0011). */
  key: string;
  label: string;
  kind: LimitKind;
  /** Seed value for new plans in the editor. `null` = unlimited. */
  default: LimitValue;
  /** Editor sort group; descriptors render grouped, in registration order. */
  group: string;
  hint?: string;
}

/** A feature toggle (Plesk plan "permission") a module contributes. */
export interface FeatureDescriptor {
  key: string;
  label: string;
  description: string;
  /** Seed value for new plans. Absent keys in stored plans read as false. */
  default: boolean;
}

/**
 * Plan limits are an open record: core keys plus whatever modules
 * registered. Values are validated here; which keys are meaningful is the
 * registry's business. Unknown keys are legal and preserved — they may
 * belong to a module that is currently disabled or no longer installed.
 */
export const planLimitsSchema = z.record(limitValueSchema);
export type PlanLimits = z.infer<typeof planLimitsSchema>;

export const planFeaturesSchema = z.record(z.boolean());
export type PlanFeatures = z.infer<typeof planFeaturesSchema>;

/** Partial overrides stored on a subscription. */
export const limitOverridesSchema = z.record(limitValueSchema);
export type LimitOverrides = z.infer<typeof limitOverridesSchema>;

/**
 * The core module's descriptors — today's limit set, registered at boot by
 * the plans module so pre-G4 data keeps working unchanged. Defaults mirror
 * what the plan editor seeded before descriptors existed.
 */
export const CORE_LIMIT_DESCRIPTORS: readonly LimitDescriptor[] = [
  { key: 'applications', label: 'Applications', kind: 'count', default: 1, group: 'Resources' },
  { key: 'databases', label: 'Databases', kind: 'count', default: 1, group: 'Resources' },
  { key: 'services', label: 'Services', kind: 'count', default: 0, group: 'Resources' },
  {
    key: 'domains',
    label: 'Hosted sites',
    kind: 'count',
    default: 1,
    group: 'Resources',
    hint: 'Canonical hosted-site names; application subdomains and aliases use their own limits',
  },
  {
    key: 'disk_mb',
    label: 'Disk',
    kind: 'megabytes',
    default: 5120,
    group: 'Quotas',
    hint: 'Total disk quota across resources; the overuse policy decides what a crossing does',
  },
  {
    key: 'memory_mb',
    label: 'Memory',
    kind: 'megabytes',
    default: 512,
    group: 'Quotas',
    hint: 'Total memory quota across resources (advisory)',
  },
  {
    key: 'cpu_cores',
    label: 'CPU cores',
    kind: 'count',
    default: 1,
    group: 'Quotas',
    hint: "CPU cores per resource, applied as Coolify's limits_cpus at provisioning time",
  },
];

export const CORE_FEATURE_DESCRIPTORS: readonly FeatureDescriptor[] = [
  {
    key: 'deployments',
    label: 'Trigger deployments',
    description: 'Tenant may trigger deployments and restarts of their resources.',
    default: true,
  },
  {
    key: 'environment_variables',
    label: 'Manage environment variables',
    description: 'Tenant may view and edit environment variables.',
    default: true,
  },
  {
    key: 'logs',
    label: 'View logs',
    description: 'Tenant may view application logs and deployment output.',
    default: true,
  },
  {
    key: 'backups',
    label: 'Included in backups',
    description: 'Tenant resources are included in scheduled backups.',
    default: false,
  },
  {
    key: 'custom_domains',
    label: 'Attach custom domains',
    description: 'Tenant may attach custom domains to their resources.',
    default: true,
  },
  {
    key: 'database_external_access',
    label: 'Expose databases on public ports',
    description:
      "Databases may be exposed on a public host port (Coolify's is_public + public_port). " +
      'Off by default — the safe direction for an internet-exposure toggle.',
    default: false,
  },
  {
    key: 'application_runtime_configuration',
    label: 'Configure application runtimes',
    description: 'Tenant may edit application build and runtime commands and directories.',
    default: true,
  },
  {
    key: 'application_health_checks',
    label: 'Configure application health checks',
    description: 'Tenant may configure application health-check policy.',
    default: true,
  },
  {
    key: 'application_scheduled_tasks',
    label: 'Manage application scheduled tasks',
    description: 'Tenant may create and operate application-scoped scheduled commands.',
    default: true,
  },
  {
    key: 'application_maintenance',
    label: 'Manage application maintenance',
    description: 'Tenant may stop and resume an application through the audited maintenance workflow.',
    default: true,
  },
];

export const UNLIMITED_LIMITS: PlanLimits = Object.fromEntries(
  CORE_LIMIT_DESCRIPTORS.map((d) => [d.key, null]),
);

export const DEFAULT_FEATURES: PlanFeatures = Object.fromEntries(
  CORE_FEATURE_DESCRIPTORS.map((d) => [d.key, d.default]),
);

/**
 * Resolve the limits that actually apply to a subscription: plan limits
 * with any per-subscription overrides applied on top. Data-driven — the
 * result covers the union of keys present in either object. Keys a stored
 * plan lacks entirely (persisted before the limit existed) stay absent;
 * readers treat absent as unlimited via `isWithinLimit`/`formatLimit`.
 */
export function effectiveLimits(
  planLimits: PlanLimits,
  overrides?: LimitOverrides | null,
): PlanLimits {
  const result: PlanLimits = {};
  for (const [key, value] of Object.entries(planLimits)) {
    result[key] = value ?? null;
  }
  for (const [key, value] of Object.entries(overrides ?? {})) {
    if (value !== undefined) result[key] = value;
  }
  return result;
}

/**
 * The features a subscription can actually use once a reseller ceiling caps
 * its plan (G20, ADR-0034): `effective = plan feature ∩ reseller ceiling`.
 *
 * - **No ceiling** (admin-owned client) → the plan features pass through
 *   unchanged. This is the compatibility guarantee: every existing,
 *   non-reseller subscription behaves byte-for-byte as before.
 * - **With a ceiling** → a feature is on only if the plan grants it AND the
 *   reseller ceiling permits it. Absent ceiling keys read as *denied*
 *   (fail-closed) — a permission the reseller was never granted must not leak
 *   through; new reseller plans seed all known feature keys so this only bites
 *   genuinely-unknown future keys until an admin widens the ceiling.
 *
 * Applied at read time like `effectiveLimits`, so editing a reseller plan's
 * ceiling propagates to every affected subscription instantly.
 */
export function effectiveFeatures(
  planFeatures: PlanFeatures,
  resellerCeiling?: PlanFeatures | null,
): PlanFeatures {
  if (!resellerCeiling) return { ...planFeatures };
  const result: PlanFeatures = {};
  for (const [key, granted] of Object.entries(planFeatures)) {
    result[key] = granted === true && resellerCeiling[key] === true;
  }
  return result;
}

/** `null` limit = unlimited; so is a key the stored plan doesn't have. */
export function isWithinLimit(limit: LimitValue | undefined, currentUsage: number): boolean {
  return limit == null || currentUsage < limit;
}

export function formatLimit(value: LimitValue | undefined): string {
  return value == null ? 'Unlimited' : String(value);
}

/** Kind-aware formatting: megabyte limits render as MB/GB. */
export function formatLimitAmount(value: LimitValue | undefined, kind: LimitKind): string {
  if (value == null) return 'Unlimited';
  if (kind === 'megabytes') {
    if (value >= 1024) {
      const gb = value / 1024;
      return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
    }
    return `${value} MB`;
  }
  return String(value);
}
